import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { EventoDraft, FormaParticipar, ModoPagamento } from './types';

export function imagemVazia() {
  return { arquivo: null, url: null, removida: false, carregando: false };
}

export function eventoDraftVazio(): EventoDraft {
  return {
    nome: '',
    slug: '',
    descricao: '',
    categoria: '',
    formaParticipar: 'inscricao',
    usaEquipes: false,
    imagemDesktop: imagemVazia(),
    imagemMobile: imagemVazia(),
    localNome: '',
    endereco: '',
    cidade: '',
    uf: '',
    cep: '',
    inicioData: '',
    inicioHora: '',
    fimData: '',
    fimHora: '',
    quemPagaTaxa: 'comprador',
    contatoNome: '',
    contatoTelefone: '',
    modoPagamento: 'plataforma',
    textoPagamento: '',
  };
}

export function novaParticipacao(): FormaParticipar {
  return {
    id: null,
    nome: '',
    descricao: '',
    publica: true,
    codigoAcesso: '',
    inscricoesAbremData: '',
    inscricoesAbremHora: '',
    inscricoesFechamData: '',
    inscricoesFechamHora: '',
    temLimite: false,
    vagas: null,
    dividirGrupos: false,
    valorCentavos: 0,
    valorModo: 'gratuito',
    grupos: [],
    temFormulario: false,
    formularioId: null,
    formularioNome: '',
    inscritos: 0,
    exigeIdentificacao: true,
    maxPorCompra: 10,
    validacaoPresencial: false,
  };
}

// Nomes sugeridos de tipos de inscrição conforme a categoria escolhida.
const SUGESTOES_POR_CATEGORIA: Record<string, string[]> = {
  acampamento: ['Campista', 'Equipe'],
  retiro: ['Participante', 'Equipe'],
  curso: ['Aluno', 'Ouvinte'],
  encontro: ['Casal', 'Equipe'],
  congresso: ['Participante', 'Estudante', 'Equipe'],
  show: ['Inteira', 'Meia-entrada'],
  formacao: ['Participante'],
  outro: ['Participante'],
};

export function sugestoesParaCategoria(categoria: string): string[] {
  return SUGESTOES_POR_CATEGORIA[categoria] || ['Participante'];
}

// Mapeia o texto salvo em `eventos.modo_recebimento` para o tipo da tela.
export function modoPagamentoDe(texto: unknown): ModoPagamento {
  if (texto === 'rifa' || texto === 'gratuito' || texto === 'direto') return texto;
  return 'plataforma';
}

export function slugificar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Monta um EventoDraft a partir de uma linha crua da tabela `eventos`.
export function draftDeEvento(ev: Record<string, unknown>): EventoDraft {
  const ini = separarDataHora((ev.inicio_em as string) || '');
  const fim = separarDataHora((ev.fim_em as string) || '');
  return {
    nome: (ev.nome as string) || '',
    slug: (ev.slug as string) || '',
    descricao: (ev.descricao as string) || '',
    categoria: (ev.categoria as EventoDraft['categoria']) || '',
    formaParticipar: (ev.vende_ingressos as boolean) ? 'ingresso' : 'inscricao',
    usaEquipes: (ev.usa_equipes as boolean) || false,
    imagemDesktop: {
      arquivo: null,
      url: (ev.imagem_desktop_url as string) || null,
      removida: false,
      carregando: false,
    },
    imagemMobile: {
      arquivo: null,
      url: (ev.imagem_mobile_url as string) || null,
      removida: false,
      carregando: false,
    },
    localNome: (ev.local_nome as string) || '',
    endereco: (ev.endereco as string) || '',
    cidade: (ev.cidade as string) || '',
    uf: (ev.uf as string) || '',
    cep: (ev.cep as string) || '',
    inicioData: ini.data,
    inicioHora: ini.hora,
    fimData: fim.data,
    fimHora: fim.hora,
    quemPagaTaxa: (ev.quem_paga_taxa as string) === 'organizador' ? 'organizador' : 'comprador',
    contatoNome: (ev.contato_nome as string) || '',
    contatoTelefone: (ev.contato_telefone as string) || '',
    modoPagamento: modoPagamentoDe(ev.modo_recebimento),
    textoPagamento: (ev.texto_pagamento as string) || '',
  };
}

export function centavosParaTexto(centavos: number): string {
  if (!centavos) return '';
  const reais = Math.floor(centavos / 100);
  const cents = centavos % 100;
  return `${reais},${String(cents).padStart(2, '0')}`;
}

export function textoParaCentavos(texto: string): number {
  const digitos = texto.replace(/\D/g, '');
  if (!digitos) return 0;
  return parseInt(digitos, 10);
}

export function mascaraDinheiro(valor: string): string {
  const digitos = valor.replace(/\D/g, '');
  if (!digitos) return '';
  const centavos = parseInt(digitos, 10);
  const reais = Math.floor(centavos / 100);
  const cents = centavos % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(cents).padStart(2, '0')}`;
}

export function mascaraCEP(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

export function mascaraCNPJ(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function dois(n: number): string {
  return String(n).padStart(2, '0');
}

export function combinarDataHora(data: string, hora: string): string | null {
  if (!data) return null;
  const h = hora || '00:00';
  const d = new Date(`${data}T${h}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function separarDataHora(iso: string): { data: string; hora: string } {
  if (!iso) return { data: '', hora: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { data: '', hora: '' };
  return {
    data: `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`,
    hora: `${dois(d.getHours())}:${dois(d.getMinutes())}`,
  };
}

export function basePathSite(): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  return basePath ? `/${basePath}` : '';
}

export function urlEvento(slug: string): string {
  return `${window.location.origin}${basePathSite()}/e/${slug}`;
}

export function urlEventoComCodigo(slug: string, codigo: string): string {
  return `${urlEvento(slug)}?c=${encodeURIComponent(codigo)}`;
}

export function rolarParaPrimeiroErro(): void {
  requestAnimationFrame(() => {
    const el = document.querySelector('[data-erro="true"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

export function mensagemErro(
  e: unknown,
  padrao = 'Não foi possível salvar. Tente novamente.',
): string {
  return mensagemAmigavel(e, padrao);
}

export function sessaoPerdida(e: unknown): boolean {
  const status =
    typeof e === 'object' && e !== null && 'status' in e
      ? Number((e as { status?: unknown }).status ?? 0)
      : 0;
  if (status === 401) return true;
  const msg = mensagemErro(e, '').toLowerCase();
  return msg.includes('jwt') || msg.includes('not authenticated');
}

export function sugerirCodigo(nome: string): string {
  const ano = String(new Date().getFullYear());
  const base = nome
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '');
  if (!base) return `ACESSO${ano}`;
  return base.endsWith(ano) ? base : `${base}${ano}`;
}

let contadorChave = 0;
export function novaChave(): string {
  contadorChave += 1;
  return `q-${Date.now()}-${contadorChave}`;
}

export function condicaoValorParaJson(v: string | null): boolean | string | null {
  if (v === null || v === '') return null;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return v;
}

export function jsonParaCondicaoValor(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (v === true) return 'true';
  if (v === false) return 'false';
  return String(v);
}